import type { RequestStatus } from '@features/request/requestSlice';
import type { BimcCalculationLimitedCostUpdateData } from '@gql/graphql';
import type { Meta } from '@utils/types';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';

import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimcCalculationLimitedCostForUpdateGql } from '../bimCalculationLimitedQueries';

interface IUseBimCalculationLimitedCostForUpdate {
    limitedCostId: number | undefined;
    calculationId: number | undefined;
}

const requestId = 'bimcCalculationLimitedCostForUpdate';

export const useBimCalculationLimitedCostForUpdate = (
    props: IUseBimCalculationLimitedCostForUpdate,
): {
    limitedForUpdateReset: (value: unknown) => { payload: undefined; type: string } & { meta: Meta };
    limitedForUpdateData: BimcCalculationLimitedCostUpdateData | null | undefined;
    limitedForUpdateStatus: RequestStatus;
} => {
    const dispatch = useDispatch();

    const { calculationId, limitedCostId } = props;

    const limitedForUpdateVariables = useMemo(
        () => ({
            calculationId: calculationId as number,
            limitedCostId: limitedCostId as number,
        }),
        [calculationId, limitedCostId],
    );

    const [limitedForUpdateData, limitedForUpdateStatus, , limitedForUpdateRequest] = useRequest({
        requestId,
        variables: limitedForUpdateVariables,
        query: bimcCalculationLimitedCostForUpdateGql,
        withEffect: false,
    });

    const [reset] = useResetStoreData({ requestId });

    useEffect(() => {
        if (limitedCostId && calculationId) {
            dispatch(limitedForUpdateRequest(limitedForUpdateVariables));
        }
    }, [calculationId, dispatch, limitedCostId, limitedForUpdateRequest, limitedForUpdateVariables]);

    return {
        limitedForUpdateReset: reset,
        limitedForUpdateData: limitedForUpdateData?.bimcCalculationLimitedCostForUpdate?.data as
            | BimcCalculationLimitedCostUpdateData
            | undefined,
        limitedForUpdateStatus,
    };
};
